import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ShieldCheck, Phone, MessageCircle, MapPin, Camera, ArrowLeft, AlertTriangle, FileSignature } from 'lucide-react';
import LocationMap from '../components/LocationMap';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { BRAND } from '../lib/brand';
import { buildWhatsAppUrl, openWhatsApp } from '../lib/whatsapp';

export default function EmergencyMode() {
  const [coords, setCoords] = useState(null);
  const [requesting, setRequesting] = useState(false);

  const requestLocation = () => {
    if (!navigator.geolocation) {
      toast.error('GPS nicht verfügbar in diesem Browser');
      return;
    }
    setRequesting(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy });
        toast.success('Standort übermittelt');
        setRequesting(false);
      },
      (err) => {
        toast.error('Standort konnte nicht ermittelt werden');
        setRequesting(false);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const whatsappText = coords
    ? `Hallo UnfallHeld, ich hatte einen Unfall und benötige Unterstützung. Mein Standort: https://maps.google.com/?q=${coords.lat},${coords.lng}`
    : 'Hallo UnfallHeld, ich hatte einen Unfall und benötige Unterstützung.';
  const whatsappHref = buildWhatsAppUrl(whatsappText);

  return (
    <div className="min-h-screen uh-emergency-bg text-white" data-testid="emergency-mode-screen">
      <header className="px-4 py-4 flex items-center justify-between">
        <Link to="/" className="inline-flex items-center gap-1 text-white/85 hover:text-white text-sm" data-testid="emergency-back-link">
          <ArrowLeft className="h-4 w-4" /> Zurück
        </Link>
        <div className="font-heading font-semibold">{BRAND.name}</div>
      </header>

      <main className="mx-auto max-w-md px-4 pt-2 pb-10 uh-safe-bottom">
        <div className="text-center">
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/10 border border-white/15 text-xs">
            <ShieldCheck className="h-3.5 w-3.5 text-orange-300" /> Notfallmodus
          </span>
          <h1 className="mt-4 font-heading font-semibold text-3xl sm:text-4xl">Unfall? Wir helfen bei der digitalen Schadenmeldung.</h1>
          <p className="mt-2 text-white/80">Teilen Sie Standort, Fotos und Fahrzeugdaten. UnfallHeld erstellt eine strukturierte Schadenakte und hilft Ihnen bei der weiteren Abwicklung.</p>
        </div>

        <Link
          to="/unfall-melden"
          state={{ emergency: true }}
          className="mt-6 w-full inline-flex items-center justify-center gap-2 min-h-[64px] rounded-2xl bg-red-600 hover:bg-red-700 text-white text-lg font-semibold uh-shadow-lg uh-pulse-emergency active:scale-[0.98] transition-colors"
          data-testid="emergency-mode-primary-button"
        >
          <FileSignature className="h-5 w-5" /> Unfall jetzt melden
        </Link>

        <div className="mt-5 grid grid-cols-2 gap-3">
          <button
            onClick={requestLocation}
            className="min-h-[60px] rounded-2xl bg-white/10 border border-white/15 hover:bg-white/15 active:scale-[0.98] transition-colors flex flex-col items-center justify-center gap-1"
            data-testid="emergency-mode-gps-button"
          >
            <MapPin className="h-5 w-5" />
            <span className="text-sm">{requesting ? 'Suche...' : 'Standort teilen'}</span>
          </button>
          <a
            href={`tel:${BRAND.phone}`}
            className="min-h-[60px] rounded-2xl bg-white/10 border border-white/15 hover:bg-white/15 active:scale-[0.98] transition-colors flex flex-col items-center justify-center gap-1"
            data-testid="emergency-mode-call-button"
          >
            <Phone className="h-5 w-5" />
            <span className="text-sm">Anrufen</span>
          </a>
          <a
            href={whatsappHref}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => { e.preventDefault(); openWhatsApp(whatsappText); }}
            className="min-h-[60px] rounded-2xl bg-[#25D366]/15 border border-[#25D366]/30 hover:bg-[#25D366]/25 active:scale-[0.98] transition-colors flex flex-col items-center justify-center gap-1"
            data-testid="emergency-mode-whatsapp-button"
          >
            <MessageCircle className="h-5 w-5" />
            <span className="text-sm">WhatsApp</span>
          </a>
          <Link
            to="/unfall-melden"
            state={{ emergency: true, focus: 'photos' }}
            className="min-h-[60px] rounded-2xl bg-white/10 border border-white/15 hover:bg-white/15 active:scale-[0.98] transition-colors flex flex-col items-center justify-center gap-1"
            data-testid="emergency-mode-photos-button"
          >
            <Camera className="h-5 w-5" />
            <span className="text-sm">Fotos hochladen</span>
          </Link>
        </div>

        {coords && (
          <div className="mt-6 rounded-2xl bg-white/5 border border-white/10 p-3">
            <div className="text-xs text-white/70 mb-2">Ihr Standort wurde übermittelt</div>
            <LocationMap lat={coords.lat} lng={coords.lng} height={220} testid="emergency-mode-map" />
            <div className="text-xs text-white/70 mt-2">
              {coords.lat.toFixed(5)}, {coords.lng.toFixed(5)} (Genauigkeit: ±{Math.round(coords.accuracy || 0)}m)
            </div>
          </div>
        )}

        <div className="mt-6 text-center text-xs text-white/70">
          Bei akuter Gefahr bitte <strong className="text-white">112</strong> (Euronotruf) • <strong className="text-white">144</strong> Rettung • <strong className="text-white">133</strong> Polizei
        </div>
      </main>
    </div>
  );
}
